import { useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface UserProfile {
  id: string;
  email?: string;
  full_name?: string;
  avatar_url?: string;
  beta_tester?: boolean;
}

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  // Fetch user profile
  const fetchUserProfile = async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
      
      if (error) {
        console.error('Error fetching profile:', error);
        return;
      }
      
      // If no profile exists, create one
      if (!data) {
        const { data: sessionData } = await supabase.auth.getSession();
        const user = sessionData?.session?.user;
        
        if (user) {
          const { data: newProfile, error: insertError } = await supabase
            .from('profiles')
            .insert({
              id: userId,
              email: user.email,
              full_name: user.user_metadata?.full_name || user.user_metadata?.name || null
            })
            .select()
            .single();
          
          if (insertError) {
            console.error('Error creating profile:', insertError);
            return;
          }
          
          setProfile(newProfile);
        }
      } else {
        setProfile(data);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        // Fetch profile data when user signs in
        if (session?.user) {
          fetchUserProfile(session.user.id);
        } else {
          setProfile(null);
        }
        
        setLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        fetchUserProfile(session.user.id);
      }
      
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      setProfile(null);
      setUser(null);
      setSession(null);
      
      // Clear all navigation state
      sessionStorage.clear();
      
      // Force reload to clear any cached state
      window.location.href = '/';
    } catch (error) {
      console.error('Error signing out:', error);
      throw error;
    }
  };

  const signIn = async (email: string, password: string) => {
    if (!email || !password) {
      throw new Error('Veuillez saisir votre adresse e-mail et votre mot de passe');
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      throw new Error('Veuillez saisir une adresse e-mail valide');
    }

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.toLowerCase().trim(),
        password,
      });

      if (error) {
        // Transform Supabase errors to user-friendly messages
        if (error.message.includes('Invalid login credentials')) {
          throw new Error('Adresse e-mail ou mot de passe incorrect. Vérifiez vos informations et réessayez.');
        }
        if (error.message.includes('Email not confirmed')) {
          throw new Error('Votre compte n\'est pas encore activé. Vérifiez votre boîte e-mail et cliquez sur le lien de confirmation.');
        }
        if (error.message.includes('Too many requests')) {
          throw new Error('Trop de tentatives de connexion. Veuillez patienter quelques minutes avant de réessayer.');
        }
        throw new Error('Connexion impossible. Vérifiez votre connexion Internet et réessayez.');
      }
      
      // Force reload after successful login to ensure fresh state
      if (data.session) {
        sessionStorage.clear();
        window.location.href = '/';
      }
      
      return { data, error: null };
    } catch (error: any) {
      console.error('Sign in error:', error);
      return { data: null, error };
    }
  };

  const signUp = async (email: string, password: string) => {
    if (!email || !password) {
      throw new Error('Veuillez saisir votre adresse e-mail et votre mot de passe');
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      throw new Error('Veuillez saisir une adresse e-mail valide');
    }

    if (password.length < 8) {
      throw new Error('Le mot de passe doit contenir au moins 8 caractères');
    }

    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(password)) {
      throw new Error('Le mot de passe doit contenir au moins une majuscule, une minuscule et un chiffre');
    }

    try {
      const redirectUrl = `${window.location.origin}/`;
      
      const { data, error } = await supabase.auth.signUp({
        email: email.toLowerCase().trim(),
        password,
        options: {
          emailRedirectTo: redirectUrl
        }
      });

      if (error) {
        // Transform Supabase errors to user-friendly messages
        if (error.message.includes('User already registered')) {
          throw new Error('Cette adresse e-mail est déjà utilisée. Connectez-vous ou utilisez une autre adresse.');
        }
        if (error.message.includes('Password should be at least')) {
          throw new Error('Le mot de passe doit contenir au moins 8 caractères');
        }
        if (error.message.includes('Invalid email')) {
          throw new Error('Veuillez saisir une adresse e-mail valide');
        }
        throw new Error('Inscription impossible. Vérifiez votre connexion Internet et réessayez.');
      }
      
      // Force reload after successful signup to ensure fresh state
      if (data.session) {
        sessionStorage.clear();
        window.location.href = '/';
      }
      
      return { data, error: null };
    } catch (error: any) {
      console.error('Sign up error:', error);
      return { data: null, error };
    }
  };

  return {
    user,
    session,
    profile,
    loading,
    signOut,
    signIn,
    signUp,
    isAuthenticated: !!user
  };
};